import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, XCircle, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_099c2006-67c6-4d2e-acf9-1e0fd2c8352f/artifacts/80f6u7mg_Logo%20Final%20Whole%202026.png" 
                alt="TrackOn Logo" 
                className="h-10 object-contain"
              />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <DollarSign className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Refund Policy</h1>
          <p className="text-xl text-red-100">
            Mohammad Alnooryani Vehicles Tracking Systems Services
          </p>
          <p className="mt-2 text-red-200">Clear and transparent refund terms</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-12">
        <Card className="mb-12 border-red-200 border-2">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">General Refund Policy</h2>
                <p className="text-gray-700 mb-3">
                  TrackOn provides subscription-based and hardware-enabled GPS tracking and fleet management solutions.
                </p>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-gray-900 font-semibold mb-2">Important: All payments are non-refundable unless explicitly stated below.</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Refunds are evaluated on a case-by-case basis</li>
                    <li>All refund requests are subject to internal approval</li>
                    <li>No automatic refunds are provided</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Non-Refundable Items</h2>
          <Card className="border-red-200">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4 font-semibold">The following items are NOT eligible for refunds:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">GPS devices once installed and activated</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">Installation charges and technician fees</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">SIRA certification fees (new or renewal)</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">Subscription fees (annual or multi-year) once service is activated</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">Platform access fees, SIM usage, or data charges</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">Services delayed due to customer non-cooperation or incomplete documentation</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Subscription Cancellation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200 border-2">
              <CardContent className="p-6">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm">1</span>
                  Customer-Initiated
                </h3>
                <p className="text-gray-700 mb-3">
                  Customers may request cancellation 2 months before contract end date.
                </p>
                <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-600">
                  <p className="text-sm text-gray-700">
                    <strong>Important:</strong> No refunds for unused subscription periods. Services remain active until end of paid term.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 border-2">
              <CardContent className="p-6">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm">2</span>
                  TrackOn-Initiated
                </h3>
                <p className="text-gray-700 mb-3">
                  TrackOn reserves the right to suspend or terminate services without refund for:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Non-payment of subscription dues</li>
                  <li>Device misuse or tampering</li>
                  <li>Violation of UAE laws or service terms</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Eligible Refund Scenarios (Limited Cases)</h2>
          <p className="text-gray-700 mb-6">
            Refunds may be considered only in the following specific situations:
          </p>
          
          <Card className="border-green-200 border-2 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-green-100 rounded-full p-3 flex-shrink-0">
                  <XCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Service Non-Activation</h3>
                  <p className="text-gray-700 mb-4">Payment received but service not activated or installed</p>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Conditions:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Refund request must be raised within 7 business days of payment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">No installation, configuration, or documentation has been initiated</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Valid only if TrackOn has not begun service delivery</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 border-2 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-green-100 rounded-full p-3 flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Duplicate or Excess Payment</h3>
                  <p className="text-gray-700 mb-4">Verified duplicate transaction or overpayment</p>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Conditions:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Payment verification and bank statement required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Refund processed to original payment source only</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Investigation may take 5-7 business days</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 border-2">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-green-100 rounded-full p-3 flex-shrink-0">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">TrackOn Service Inability</h3>
                  <p className="text-gray-700 mb-4">TrackOn unable to deliver committed service due to internal limitations</p>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Conditions:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Subject to management review and approval</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Full documentation of inability to deliver required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">Case-by-case evaluation</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Request a Refund</h2>
          <Card className="bg-blue-50">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                Customers must submit a written refund request through official TrackOn channels with:
              </p>
              <div className="bg-white p-4 rounded-lg mb-4">
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-red-600">1.</span>
                    <span>Company/Customer Name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-red-600">2.</span>
                    <span>Invoice Number</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-red-600">3.</span>
                    <span>Payment Reference/Transaction ID</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-red-600">4.</span>
                    <span>Detailed Reason for Refund Request</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-red-600">5.</span>
                    <span>Bank Account Details (for refund)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-blue-300">
                <p className="text-sm text-gray-700">
                  <strong>Submit requests to:</strong> <a href="mailto:gps@nooryanivts.com" className="text-red-600 hover:underline">gps@nooryanivts.com</a> or call <a href="tel:+971508607793" className="text-red-600 hover:underline">+971 50 860 7793</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Refund Processing Timeline</h2>
          <Card className="border-purple-200 border-2">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <FileText className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">Step 1: Review</h3>
                  <p className="text-gray-600 text-sm">Request reviewed by management (3-5 business days)</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">Step 2: Approval</h3>
                  <p className="text-gray-600 text-sm">Customer notified of decision (email/phone)</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <DollarSign className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">Step 3: Processing</h3>
                  <p className="text-gray-600 text-sm">Approved refunds processed within 7-14 business days</p>
                </div>
              </div>
              <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-300">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Actual credit to bank account may take additional time depending on your bank. Processing delays beyond TrackOn's control are not our responsibility.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Chargebacks & Payment Disputes</h2>
          <Card className="bg-orange-50 border-orange-300 border-2">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-8 w-8 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-4">
                    Customers are encouraged to contact TrackOn directly before initiating any chargeback or payment dispute with their bank.
                  </p>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-orange-600">
                    <p className="text-gray-900 font-semibold mb-2">Important Warning:</p>
                    <p className="text-gray-700 text-sm">
                      Unjustified chargebacks may result in immediate service suspension, account termination, and potential legal action. TrackOn reserves the right to recover costs associated with defending against fraudulent chargebacks.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mb-12">
          <Card className="bg-gray-100">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Policy Amendments</h3>
              <p className="text-gray-700 text-sm mb-4">
                TrackOn reserves the right to modify or update this Refund Policy at any time. Changes will apply prospectively without prior notice. The latest published version is the final and applicable version.
              </p>
              <h3 className="font-bold text-lg text-gray-900 mb-3 mt-6">Governing Law & Jurisdiction</h3>
              <p className="text-gray-700 text-sm">
                This Refund Policy is governed by the laws of the United Arab Emirates. Any disputes arising from refund requests shall fall under the exclusive jurisdiction of Dubai courts.
              </p>
            </CardContent>
          </Card>
        </section>

        <Card className="bg-gradient-to-br from-red-600 to-red-800 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Questions About Refunds?</h3>
            <p className="mb-6 text-red-100">
              Contact our team before initiating any payment disputes
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+971508607793">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Call: +971 50 860 7793
                </Button>
              </a>
              <a href="mailto:gps@nooryanivts.com">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100">
                  Email: gps@nooryanivts.com
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 TrackOn - Mohammad Alnooryani Vehicles Tracking Systems Services. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/warranty-policy" className="text-gray-400 hover:text-white">Warranty Policy</Link>
            <Link to="/service-policy" className="text-gray-400 hover:text-white">Service Policy</Link>
            <Link to="/refund-policy" className="text-red-400 hover:text-red-300">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicy;
